import { z } from 'zod';
import type { TFunction } from 'i18next';
import {
  OFFER_EMBED_CODE_MAX_LENGTH,
  type OfferEmbedCodeIssue,
  getOfferEmbedCodeIssue,
} from '@/lib/offerEmbed';
import {
  INSTALLMENT_COUNT_MAX,
  INSTALLMENT_COUNT_MIN,
  type StripePriceOptionsIssue,
  getStripePriceOptionsIssue,
} from '@/lib/stripePriceOptions';

const STRIPE_PRICES_ISSUE_MESSAGES: Record<StripePriceOptionsIssue, string> = {
  required: 'offersForm.validation.stripePriceRequired',
  duplicate: 'offersForm.validation.stripePriceDuplicate',
};

const stripePriceOptionSchema = z.object({
  priceId: z.string(),
  installmentCount: z
    .number()
    .int()
    .min(INSTALLMENT_COUNT_MIN)
    .max(INSTALLMENT_COUNT_MAX)
    .nullable(),
});

const EMBED_CODE_ISSUE_MESSAGES: Record<OfferEmbedCodeIssue, string> = {
  unsupportedProvider: 'offersForm.validation.embedCodeProviderUnsupported',
  stripePricingTable: 'offersForm.validation.embedCodeStripePricingTableUnsupported',
  whopOptions: 'offersForm.validation.embedCodeWhopOptionsUnsupported',
  kalleoUrl: 'offersForm.validation.embedCodeKalleoUrlInvalid',
};

export const makeOfferSchema = (t: TFunction) =>
  z
    .object({
      privateTitle: z
        .string()
        .min(1, t('offersForm.validation.titleRequired'))
        .max(200, t('offersForm.validation.titleMax', { max: 200 }))
        .refine((val) => val.trim().length > 0, {
          message: t('offersForm.validation.titleRequired'),
        }),
      abTesting: z.boolean().nullable().optional(),
      splitPercentA: z.number().min(0).max(100).nullable().optional(),

      titleA: z
        .string()
        .min(1, t('offersForm.validation.titleRequired'))
        .max(35, t('offersForm.validation.titleMax', { max: 35 }))
        .refine((val) => val.trim().length > 0, {
          message: t('offersForm.validation.titleRequired'),
        }),
      subTitleA: z
        .string()
        .max(35, t('offersForm.validation.subtitleMax', { max: 35 }))
        .nullable()
        .optional()
        .refine((val) => !val || val.trim().length === 0 || val.trim().length > 0, {
          message: t('offersForm.validation.subtitleNotEmpty'),
        }),
      descriptionA: z
        .string()
        .max(100, t('offersForm.validation.descriptionMax', { max: 100 }))
        .nullable()
        .optional()
        .refine((val) => !val || val.trim().length === 0 || val.trim().length > 0, {
          message: t('offersForm.validation.descriptionNotEmpty'),
        }),
      buttonLabelA: z
        .string()
        .min(1, t('offersForm.validation.buttonTextRequired'))
        .max(35, t('offersForm.validation.buttonTextMax', { max: 35 }))
        .refine((val) => val.trim().length > 0, {
          message: t('offersForm.validation.buttonTextRequired'),
        }),
      conversionModeA: z.enum(['EXTERNAL_LINK', 'EMBED', 'STRIPE_EMBEDDED_CHECKOUT']),
      buttonLinkA: z.string().max(1024, t('offersForm.validation.buttonLinkMax', { max: 1024 })),
      embedCodeA: z
        .string()
        .max(
          OFFER_EMBED_CODE_MAX_LENGTH,
          t('offersForm.validation.embedCodeMax', { max: OFFER_EMBED_CODE_MAX_LENGTH }),
        ),
      stripePricesA: z.array(stripePriceOptionSchema),
      backgroundColorA: z.string(),
      borderColorA: z.string(),
      titleColorA: z.string(),
      textColorA: z.string(),
      buttonColorA: z.string(),
      buttonTextColorA: z.string(),
      iconA: z.string().nullable().optional(),

      showImageA: z.boolean(),
      imgA: z.union([z.instanceof(File), z.string()]).optional(),
      showIconA: z.boolean(),
      customBackgroundColorA: z.string().optional(),
      customBorderColorA: z.string().optional(),
      customButtonColorA: z.string().optional(),
      iconColorA: z.string().optional(),
      customIconColorA: z.string().optional(),
      iconBackgroundColorA: z.string().optional(),
      customIconBackgroundColorA: z.string().optional(),

      bPercent: z.number().min(0).max(100),

      titleB: z
        .string()
        .max(35, t('offersForm.validation.titleBMax', { max: 35 }))
        .optional()
        .refine((val) => !val || val.trim().length > 0, {
          message: t('offersForm.validation.titleBRequired'),
        }),
      subTitleB: z
        .string()
        .max(35, t('offersForm.validation.subtitleBMax', { max: 35 }))
        .nullable()
        .optional()
        .refine((val) => !val || val.trim().length === 0 || val.trim().length > 0, {
          message: t('offersForm.validation.subtitleBNotEmpty'),
        }),
      descriptionB: z
        .string()
        .max(100, t('offersForm.validation.descriptionBMax', { max: 100 }))
        .nullable()
        .optional()
        .refine((val) => !val || val.trim().length === 0 || val.trim().length > 0, {
          message: t('offersForm.validation.descriptionBNotEmpty'),
        }),
      buttonLabelB: z
        .string()
        .max(35, t('offersForm.validation.buttonTextBMax', { max: 35 }))
        .optional()
        .refine((val) => !val || val.trim().length > 0, {
          message: t('offersForm.validation.buttonTextBRequired'),
        }),
      conversionModeB: z.enum(['EXTERNAL_LINK', 'EMBED', 'STRIPE_EMBEDDED_CHECKOUT']),
      buttonLinkB: z
        .string()
        .max(1024, t('offersForm.validation.buttonLinkBMax', { max: 1024 }))
        .optional(),
      embedCodeB: z
        .string()
        .max(
          OFFER_EMBED_CODE_MAX_LENGTH,
          t('offersForm.validation.embedCodeMax', { max: OFFER_EMBED_CODE_MAX_LENGTH }),
        )
        .optional(),
      stripePricesB: z.array(stripePriceOptionSchema).optional(),
      backgroundColorB: z.string().optional(),
      borderColorB: z.string().optional(),
      titleColorB: z.string().optional(),
      textColorB: z.string().optional(),
      buttonColorB: z.string().optional(),
      buttonTextColorB: z.string().optional(),
      iconB: z.string().nullable().optional(),

      showImageB: z.boolean().optional(),
      imgB: z.union([z.instanceof(File), z.string()]).optional(),
      showIconB: z.boolean().optional(),
      customBackgroundColorB: z.string().optional(),
      customBorderColorB: z.string().optional(),
      customButtonColorB: z.string().optional(),
      iconColorB: z.string().optional(),
      customIconColorB: z.string().optional(),
      iconBackgroundColorB: z.string().optional(),
      customIconBackgroundColorB: z.string().optional(),
    })
    .refine(
      (data) => data.conversionModeA !== 'EXTERNAL_LINK' || data.buttonLinkA.trim().length > 0,
      {
        message: t('offersForm.validation.buttonLinkRequired'),
        path: ['buttonLinkA'],
      },
    )
    .refine(
      (data) =>
        data.conversionModeA !== 'EXTERNAL_LINK' ||
        !data.buttonLinkA.trim() ||
        data.buttonLinkA.trim().startsWith('https://'),
      {
        message: t('offersForm.validation.buttonLinkInvalidUrl'),
        path: ['buttonLinkA'],
      },
    )
    .refine((data) => data.conversionModeA !== 'EMBED' || data.embedCodeA.trim().length > 0, {
      message: t('offersForm.validation.embedCodeRequired'),
      path: ['embedCodeA'],
    })
    .superRefine((data, ctx) => {
      const stripePrices = [
        { mode: data.conversionModeA, options: data.stripePricesA, path: 'stripePricesA' },
        ...(data.abTesting
          ? [
              {
                mode: data.conversionModeB,
                options: data.stripePricesB ?? [],
                path: 'stripePricesB',
              },
            ]
          : []),
      ];
      for (const { mode, options, path } of stripePrices) {
        const issue =
          mode === 'STRIPE_EMBEDDED_CHECKOUT' ? getStripePriceOptionsIssue(options) : null;
        if (issue) {
          ctx.addIssue({
            code: 'custom',
            message: t(STRIPE_PRICES_ISSUE_MESSAGES[issue]),
            path: [path],
          });
        }
      }
    })
    .superRefine((data, ctx) => {
      const embedCodes = [
        { mode: data.conversionModeA, code: data.embedCodeA, path: 'embedCodeA' },
        ...(data.abTesting
          ? [{ mode: data.conversionModeB, code: data.embedCodeB ?? '', path: 'embedCodeB' }]
          : []),
      ];
      for (const { mode, code, path } of embedCodes) {
        const issue = mode === 'EMBED' ? getOfferEmbedCodeIssue(code) : null;
        if (issue) {
          ctx.addIssue({
            code: 'custom',
            message: t(EMBED_CODE_ISSUE_MESSAGES[issue]),
            path: [path],
          });
        }
      }
    })
    .refine(
      (data) =>
        data.conversionModeB !== 'EXTERNAL_LINK' ||
        !data.buttonLinkB?.trim() ||
        data.buttonLinkB.trim().startsWith('https://'),
      {
        message: t('offersForm.validation.buttonLinkBInvalidUrl'),
        path: ['buttonLinkB'],
      },
    )
    .refine(
      (data) =>
        !data.abTesting ||
        data.conversionModeB !== 'EMBED' ||
        (data.embedCodeB ?? '').trim().length > 0,
      {
        message: t('offersForm.validation.embedCodeRequired'),
        path: ['embedCodeB'],
      },
    )
    .refine(
      (data) => {
        if (data.showImageA && (!data.imgA || data.imgA === '')) {
          return false;
        }
        return true;
      },
      {
        message: t('offersForm.validation.imageRequired'),
        path: ['imgA'],
      },
    )
    .refine(
      (data) => {
        if (data.showImageB && (!data.imgB || data.imgB === '')) {
          return false;
        }
        return true;
      },
      {
        message: t('offersForm.validation.imageRequired'),
        path: ['imgB'],
      },
    )
    .refine(
      (data) => {
        if (data.abTesting) {
          return data.titleB && data.titleB.trim().length > 0;
        }
        return true;
      },
      {
        message: t('offersForm.validation.titleBRequired'),
        path: ['titleB'],
      },
    )
    .refine(
      (data) => {
        if (data.abTesting) {
          return data.buttonLabelB && data.buttonLabelB.trim().length > 0;
        }
        return true;
      },
      {
        message: t('offersForm.validation.buttonTextBRequired'),
        path: ['buttonLabelB'],
      },
    )
    .refine(
      (data) => {
        if (data.abTesting && data.conversionModeB === 'EXTERNAL_LINK') {
          return data.buttonLinkB && data.buttonLinkB.trim().length > 0;
        }
        return true;
      },
      {
        message: t('offersForm.validation.buttonLinkBRequired'),
        path: ['buttonLinkB'],
      },
    )
    .refine(
      (data) => {
        if (data.abTesting) {
          return (
            data.titleB &&
            data.titleB.length >= 1 &&
            data.buttonLabelB &&
            data.buttonLabelB.length >= 1 &&
            (data.conversionModeB === 'EMBED'
              ? (data.embedCodeB ?? '').trim().length >= 1
              : data.conversionModeB === 'STRIPE_EMBEDDED_CHECKOUT'
                ? getStripePriceOptionsIssue(data.stripePricesB ?? []) === null
                : data.buttonLinkB && data.buttonLinkB.length >= 1)
          );
        }
        return true;
      },
      {
        message: t('offersForm.validation.allBFieldsRequired'),
        path: ['abTesting'],
      },
    );

export type OfferFormValues = z.infer<ReturnType<typeof makeOfferSchema>>;
